import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  LuArrowLeft,
  LuCog,
  LuPackageCheck,
  LuPenSquare,
} from "react-icons/lu";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import TurningReceiveList from "../../components/turningReceives/TurningReceiveList";
import DetailAccountInfo from "../../components/materialLines/DetailAccountInfo";
import DetailHeader from "../../components/materialLines/DetailHeader";
import DetailPartyInfo from "../../components/materialLines/DetailPartyInfo";
import DetailSection from "../../components/materialLines/DetailSection";
import MaterialPhoto from "../../components/materialLines/MaterialPhoto";
import OrderStatusBadge from "../../components/materialLines/OrderStatusBadge";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { LoadingState } from "../../components/ui/TableStateRow";
import cx from "../../components/ui/cx";
import {
  detailCardClass,
  detailInfoLabelClass,
  detailTableHeadRowClass,
  detailTableWrapClass,
  mobileCardClass,
  moneyCellClass,
  pageTitleClass,
  tableHeadCellClass,
} from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { getSizeUnit } from "../../features/appSettings/appSettingsSlice";
import {
  clearCurrentTurningOrder,
  fetchTurningOrderDetail,
  getCurrentTurningOrder,
  getCurrentTurningOrderLoading,
  refreshTurningOrderDetail,
} from "../../features/turningOrders/turningOrdersSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { updatePdfFile } from "../../features/settings/settingsSlice";
import { deleteTurningReceive } from "../../services/turningReceive.service";
import { makeReport } from "../../services/download.service";
import {
  formatLength,
  lineAmount,
  lineName,
  rateSourceLabel,
} from "../../static/cuttingLine";
import {
  formatAmount,
  formatGujaratiLongDateTime,
  formatQty,
} from "../../static/materialLine";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { BreadcrumbType } from "../../types/common";
import { TurningOrderItemProps } from "../../types/turningOrder";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "ટર્નિંગ ઓર્ડર", href: route.turningOrders },
  { label: "ટર્નિંગ ઓર્ડર વિગતો" },
];

const cardRowClass = "flex items-start justify-between gap-4";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

const closedBadgeClass = "bg-green-100 text-green-800 hover:bg-green-100";

const totalCellClass = "font-bold text-[#113F67]";

const sum = (
  items: TurningOrderItemProps[],
  pick: (item: TurningOrderItemProps) => number
) => items.reduce((total, item) => total + (Number(pick(item)) || 0), 0);

const pendingClass = (item: TurningOrderItemProps) =>
  (item.pending_qty || 0) > 0 ? "text-orange-600" : "text-gray-400";

const amountOf = (item: TurningOrderItemProps) =>
  item.rate !== undefined ? lineAmount(item.qty, item.rate) : 0;

const TurningOrderDetailPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const order = useAppSelector(getCurrentTurningOrder);
  const isLoading = useAppSelector(getCurrentTurningOrderLoading);
  const loggingUser = useAppSelector(getLoggingUser);
  const sizeUnit = useAppSelector(getSizeUnit);

  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.turningOrdersUpdate
  );
  const canReport = hasPermission(
    loggingUser?.permissions,
    permission.turningOrdersReport
  );
  const withRates = hasPermission(
    loggingUser?.permissions,
    permission.turningRate
  );

  const [isReportLoading, setIsReportLoading] = useState<boolean>(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchTurningOrderDetail(Number(id)));
    }
    return () => {
      dispatch(clearCurrentTurningOrder());
    };
  }, [id, dispatch]);

  const items = useMemo(() => order?.items || [], [order]);
  const receives = order?.receives || [];

  // Back to where the user came from, or to the order list when the page was opened directly.
  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate(route.turningOrders);
    }
  };

  // A line's cut length, or "-" when the item page had none (turning needs none).
  const cutLengthText = (item: TurningOrderItemProps) =>
    item.cut_length !== null ? formatLength(item.cut_length, sizeUnit) : "-";

  const handleReportExport = async () => {
    setIsReportLoading(true);
    const file = await makeReport(`/v1/admin/turning-orders/${id}/pdf`);
    setIsReportLoading(false);

    if (file) dispatch(updatePdfFile(file));
  };

  // Deleting a receive changes what the order received and maybe its status.
  const handleDeleteReceive = async (receiveId: number) => {
    if (await deleteTurningReceive(receiveId)) {
      await dispatch(refreshTurningOrderDetail(Number(id)));
    }
  };

  const photo = (item: TurningOrderItemProps) => (
    <MaterialPhoto
      name={item.item?.name || "-"}
      imageUrl={item.item?.image_url}
      className="h-10 w-10 shrink-0 rounded-md"
      compact
    />
  );

  const itemText = (item: TurningOrderItemProps) =>
    lineName(item.item?.name, item.size?.name, sizeUnit);

  // A receive closed the line before all of it came, so nothing is pending on it.
  const closedBadge = (item: TurningOrderItemProps) =>
    item.closed && (
      <Badge variant="none" className={closedBadgeClass}>
        બંધ
      </Badge>
    );

  const rateText = (item: TurningOrderItemProps) =>
    item.rate !== undefined && (
      <span className="block text-xs font-normal text-gray-500">
        ₹{formatAmount(item.rate)} • {rateSourceLabel(item.rate_source)}
      </span>
    );

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>ટર્નિંગ ઓર્ડર વિગતો</h1>
      </div>

      {isLoading ? (
        <div className={detailCardClass}>
          <LoadingState />
        </div>
      ) : !order ? (
        <div className={cx(detailCardClass, "text-center text-gray-500")}>
          ટર્નિંગ ઓર્ડર ની વિગતો મળી નથી
        </div>
      ) : (
        <div className={detailCardClass}>
          <DetailHeader icon={LuCog} title="ટર્નિંગ ઓર્ડર" id={order.id}>
            <Button variant="outline" className="max-sm:flex-1" onClick={handleBack}>
              <LuArrowLeft className="h-4 w-4" />
              પાછા જાઓ
            </Button>

            {canReport && (
              <Button
                variant="primary"
                className="max-sm:flex-1"
                disabled={isReportLoading}
                onClick={handleReportExport}
              >
                {isReportLoading ? "રિપોર્ટ બની રહ્યો છે..." : "રિપોર્ટ બનાવો"}
              </Button>
            )}

            {canUpdate && (
              <Button
                variant="primary"
                className="max-sm:flex-1"
                onClick={() =>
                  navigate(
                    route.turningOrderEdit.replace(
                      ":id",
                      order.id as unknown as string
                    )
                  )
                }
              >
                <LuPenSquare className="h-4 w-4" />
                એડિટ કરો
              </Button>
            )}
          </DetailHeader>

          <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-2">
            <div className="flex flex-wrap items-center gap-x-2">
              <span className={detailInfoLabelClass}>તારીખ:</span>
              <span>{formatGujaratiLongDateTime(order.date)}</span>
            </div>

            <div className="flex items-center gap-2">
              <span className={detailInfoLabelClass}>સ્થિતિ:</span>
              <OrderStatusBadge status={order.status} />
            </div>
          </div>

          <DetailPartyInfo partyId={order.party_id} party={order.party} />

          <DetailSection title="ટર્નિંગ ઓર્ડર આઇટમ્સ ની માહિતી">
            <div className={cx(detailTableWrapClass, "max-sm:hidden")}>
              <Table>
                <TableHeader>
                  <TableRow className={detailTableHeadRowClass}>
                    <TableHead className={tableHeadCellClass}>ક્રમ</TableHead>
                    <TableHead className={tableHeadCellClass}>ફોટો</TableHead>
                    <TableHead className={tableHeadCellClass}>આઈટમ</TableHead>
                    <TableHead className={tableHeadCellClass}>
                      કટીંગ લંબાઈ
                    </TableHead>
                    <TableHead className={tableHeadCellClass}>મળેલ</TableHead>
                    <TableHead className={tableHeadCellClass}>બાકી</TableHead>
                    {withRates && (
                      <TableHead className={cx(tableHeadCellClass, "text-right")}>
                        રકમ
                      </TableHead>
                    )}
                    <TableHead className={cx(tableHeadCellClass, "text-right")}>
                      જથ્થો
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{index + 1}</TableCell>
                      <TableCell>{photo(item)}</TableCell>
                      <TableCell>
                        <span className="block break-words font-semibold text-[#113F67]">
                          {itemText(item)}
                        </span>
                        <span className="block text-xs text-gray-500">
                          {item.material?.name || "-"}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium">
                        {cutLengthText(item)}
                      </TableCell>
                      <TableCell className="font-semibold text-green-700">
                        {formatQty(item.received_qty)}
                      </TableCell>
                      <TableCell className={cx("font-semibold", pendingClass(item))}>
                        <span className="inline-flex items-center gap-2">
                          {formatQty(item.pending_qty)}
                          {closedBadge(item)}
                        </span>
                      </TableCell>
                      {withRates && (
                        <TableCell className={moneyCellClass}>
                          {formatAmount(amountOf(item))}
                          {rateText(item)}
                        </TableCell>
                      )}
                      <TableCell className="text-right text-lg font-bold">
                        {formatQty(item.qty)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                <TableFooter>
                  <TableRow className="bg-[#113F67]/10 hover:bg-[#113F67]/10">
                    <TableCell colSpan={4} className={cx(totalCellClass, "text-right")}>
                      કુલ:
                    </TableCell>
                    <TableCell className={totalCellClass}>
                      {formatQty(sum(items, (item) => item.received_qty || 0))}
                    </TableCell>
                    <TableCell className={totalCellClass}>
                      {formatQty(sum(items, (item) => item.pending_qty || 0))}
                    </TableCell>
                    {withRates && (
                      <TableCell className={cx(totalCellClass, "text-right")}>
                        {formatAmount(sum(items, amountOf))}
                      </TableCell>
                    )}
                    <TableCell className={cx(totalCellClass, "text-right text-lg")}>
                      {formatQty(sum(items, (item) => item.qty))}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>

            <div className="space-y-4 sm:hidden">
              {items.map((item, index) => (
                <div key={item.id} className={mobileCardClass}>
                  <div className="flex items-center gap-3 border-b p-4">
                    {photo(item)}
                    <span className="min-w-0">
                      <span className="block break-words font-semibold text-[#113F67]">
                        {itemText(item)}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {item.material?.name || "-"}
                      </span>
                    </span>
                  </div>

                  <div className="space-y-3 p-4 text-sm">
                    <div className={cardRowClass}>
                      <span className={cardLabelClass}>ક્રમ</span>
                      <span className="font-medium">{index + 1}</span>
                    </div>

                    <div className={cardRowClass}>
                      <span className={cardLabelClass}>કટીંગ લંબાઈ</span>
                      <span className="font-medium">
                        {cutLengthText(item)}
                      </span>
                    </div>

                    <div className={cardRowClass}>
                      <span className={cardLabelClass}>મળેલ / બાકી</span>
                      <span className="inline-flex items-center justify-end gap-2 text-right font-semibold">
                        {closedBadge(item)}
                        <span>
                          <span className="text-green-700">
                            {formatQty(item.received_qty)}
                          </span>
                          {" / "}
                          <span className={pendingClass(item)}>
                            {formatQty(item.pending_qty)}
                          </span>
                        </span>
                      </span>
                    </div>

                    {withRates && (
                      <div className={cardRowClass}>
                        <span className={cardLabelClass}>રકમ</span>
                        <span className={cx(moneyCellClass, "text-right")}>
                          {formatAmount(amountOf(item))}
                          {rateText(item)}
                        </span>
                      </div>
                    )}

                    <div className={cardRowClass}>
                      <span className={cardLabelClass}>જથ્થો</span>
                      <span className={moneyCellClass}>{formatQty(item.qty)}</span>
                    </div>
                  </div>
                </div>
              ))}

              <div className={cx(mobileCardClass, "space-y-3 p-4 text-sm")}>
                <div className={cardRowClass}>
                  <span className={totalCellClass}>કુલ નંગ</span>
                  <span className={cx(totalCellClass, "text-right")}>
                    {formatQty(sum(items, (item) => item.qty))}
                  </span>
                </div>
                {withRates && (
                  <div className={cardRowClass}>
                    <span className={totalCellClass}>કુલ રકમ</span>
                    <span className={cx(totalCellClass, "text-right")}>
                      {formatAmount(sum(items, amountOf))}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </DetailSection>

          <DetailSection title="રિસીવ ની માહિતી" icon={LuPackageCheck}>
            <TurningReceiveList
              embedded
              receives={receives}
              pageNumber={1}
              pageSize={receives.length || 1}
              isLoading={false}
              emptyLabel="આ ઓર્ડર ની હજુ કોઈ રિસીવ નથી"
              handleDeleteReceive={handleDeleteReceive}
            />
          </DetailSection>

          <DetailAccountInfo
            creator={order.creator}
            updater={order.updater}
            createdAt={order.created_at}
            updatedAt={order.updated_at}
          />
        </div>
      )}
    </AppLayout>
  );
};
export default TurningOrderDetailPage;
